// Action button handlers (copy, download, log response, go to PubMed)

import { getAuthHeaders, getAccessToken, getSubscriptionStatusUrl, authenticatedFetch } from '../api/auth.js';
import { htmlToPlainText } from './html.js';
import { openSignInModal } from './signin-modal.js';
import { getCurrentResearchId } from '../state/research.js';
import { capture as posthogCapture } from '../analytics/posthog.js';
import { ensurePdfLibsLoaded } from './pdf-loader.js';

export function logResponse(responseType, timestamp) {
    const query = document.getElementById('searchInput').value;
    const response = document.getElementById(`output-${timestamp}`).textContent;
    
    const msg = response.replace(/(\n\n\n)/g, '\n\nBMS-Marker').replace(/(References\n\n)/g, 'References\n\nBMS-Marker').replace(/(\n\n)/g, '\n').replace(/(\n)/g, '\n\n');
    const replacements = ["1. ", "2. ", "3. "];
    const output = msg.split(/BMS-Marker/).map((line, index) => {
                            return line + (replacements[index] ? replacements[index] : ""); 
                        }).join(''); 

    if(responseType === 'Positive') {
    document.getElementById(`thumbsup-${timestamp}`).innerHTML = '<span class="material-symbols-rounded notranslate">sentiment_satisfied</span>';
    document.getElementById(`thumbsdown-${timestamp}`).setAttribute("hidden", "true");
    } else if(responseType === 'Negative') {
    document.getElementById(`thumbsdown-${timestamp}`).innerHTML = '<span class="material-symbols-rounded notranslate">sentiment_dissatisfied</span>';
    document.getElementById(`thumbsup-${timestamp}`).setAttribute("hidden", "true");
    }

    const researchId = getCurrentResearchId();
    const params = new URLSearchParams({ button_click: responseType, search_id: timestamp });
    if (researchId) params.set('research_id', researchId);
    if (query) params.set('query', query);

    fetch(`/log_response?${params.toString()}`, {
        method: 'GET',
        headers: getAuthHeaders()
    });

    posthogCapture('search_feedback', {
        sentiment: responseType.toLowerCase(),
        search_id: timestamp,
        ...(researchId && { research_id: researchId }),
        ...(query && { query }),
    });
}

export async function goToPubMed(searchId, currentResearchId) {
    const token = getAccessToken();

    // Anonymous visitors on a shared research page (/share/{id}) still have a
    // currentResearchId, and the backend resolves PubMed data via the owner's
    // session in that case — only gate on sign-in when there's no research
    // context to fall back on (a fresh, unpersisted anonymous search).
    if (!token && !currentResearchId) {
        openSignInModal();
        return;
    }

    try {
        let pubmedUrl = `/pubmed-url?searchId=${encodeURIComponent(searchId)}`;
        if (currentResearchId) {
            pubmedUrl += `&research_id=${encodeURIComponent(currentResearchId)}`;
        }
        const response = await fetch(pubmedUrl, {
            headers: getAuthHeaders()
        });
        const data = await response.json();

        if (data.pubmedUrl) {
            window.open(data.pubmedUrl, '_blank');
        } else {
            console.error('PubMed URL not found for searchId:', searchId);
            alert('Unable to retrieve PubMed URL. Please try again.');
        }
    } catch (error) {
        console.error('Error fetching PubMed URL:', error);
        alert('Unable to retrieve PubMed URL. Please try again.');
    }

    fetch(`/log_response?button_click=${encodeURIComponent("Search PubMed")}`, {
        method: 'GET',
        headers: getAuthHeaders()
    });
}

export function copyText(button) {
    // Extract timestamp from button ID
    const timestamp = button.id.replace('copybtn-', '');
    const outputElement = document.getElementById(`output-${timestamp}`);
    
    if (!outputElement) {
        console.error('Output element not found for timestamp:', timestamp);
        return;
    }
    
    // Convert HTML to plain text while preserving list structure
    let responseText = htmlToPlainText(outputElement);
    
    // Get References section content if available
    const referencesSection = document.getElementById(`references-${timestamp}`);
    let referencesText = '';
    if (referencesSection && !referencesSection.classList.contains('hidden')) {
        // Clone so we can strip the "Summarize" button before reading textContent
        const clonedReferences = referencesSection.cloneNode(true);
        clonedReferences.querySelectorAll('.abstract-insight-btn').forEach((btn) => btn.remove());

        // Extract list items and add spacing between them
        const listItems = clonedReferences.querySelectorAll('li');
        if (listItems.length > 0) {
            referencesText = '\n\nReferences\n\n';
            listItems.forEach((item, index) => {
                referencesText += `${index + 1}. ${item.textContent.trim()}`;
                if (index < listItems.length - 1) {
                    referencesText += '\n\n';
                }
            });
        } else {
            referencesText = '\n\n' + clonedReferences.textContent;
        }
    }
    
    // Remove "References" section from response text if it exists, since we add it separately
    const responseWithoutReferences = responseText.replace(/\n\nReferences[\s\S]*$/i, '').trim();
    
    const finalText = responseWithoutReferences + referencesText + "\n\nGenerated using Bio-Med-Search (https://www.bio-med-search.ai)\n\nThis document summarizes findings from PubMed-indexed literature and is intended for informational and research purposes only. It does not constitute medical advice.";

    // Copy the text to clipboard
    navigator.clipboard.writeText(finalText);

    button.innerHTML = '<span class="material-symbols-rounded notranslate">check</span>';
    setTimeout(() => {
        button.innerHTML = '<span class="material-symbols-rounded notranslate">content_copy</span>';
    }, 2000);

    fetch(`/log_response?button_click=${encodeURIComponent("Copy Response")}`, {
        method: 'GET',
        headers: getAuthHeaders()
    });
}

export async function download(button) {
    // Extract timestamp from button ID
    const timestamp = button.id.replace('downloadbtn-', '');
    const el = document.getElementById(`output-${timestamp}`);
    if (!el) return;

    await ensurePdfLibsLoaded();

    // Get References section content if available
    const referencesSection = document.getElementById(`references-${timestamp}`);
    let referencesHTML = '';
    if (referencesSection && !referencesSection.classList.contains('hidden')) {
        // Clone the references section to avoid modifying the original
        const clonedSection = referencesSection.cloneNode(true);
        // Strip the "Summarize" button — pdfmake renders its icon ligature/label as plain text
        clonedSection.querySelectorAll('.abstract-insight-btn').forEach((btn) => btn.remove());
        // Add margin-top to ordered list for spacing after heading
        const ol = clonedSection.querySelector('ol');
        if (ol) {
            ol.style.marginTop = '12px';
        }
        // Add margin-bottom to list items for spacing between citations
        const listItems = clonedSection.querySelectorAll('li');
        listItems.forEach(item => {
            item.style.marginBottom = '12px';
        });
        referencesHTML = clonedSection.innerHTML;
    }

    const disclaimerHTML = `
<div style="
  font-size: 0.75em; 
  font-style: italic; 
  color: #555; 
  text-align: center; 
  margin-top: 40px;
  line-height: 1.4;
">
  <p style="margin: 0;">
    Response generated using 
    <a href="https://www.bio-med-search.ai" target="_blank">https://www.bio-med-search.ai</a><br>
    This document summarizes findings from PubMed-indexed literature and is intended for informational and research purposes only. It does not constitute medical advice.
  </p>
</div>
`;

  const html = el.innerHTML + (referencesHTML ? referencesHTML : '') + disclaimerHTML;

  // Convert HTML -> pdfmake document structure
  const content = htmlToPdfmake(html, { window: window });

  const docDefinition = {
    content: content,
    defaultStyle: {
      // fallback styles — pdfMake has different styling model
      fontSize: 11
    }
  };

  pdfMake.createPdf(docDefinition).download('bio-med-search-response.pdf');

  fetch(`/log_response?button_click=${encodeURIComponent("Download PDF")}`, {
      method: 'GET',
      headers: getAuthHeaders()
  });
}
